import {useCallback} from 'react';
import {
    Face,
    setFaceHeight,
} from 'components/customer/Product/Drawer/helpers/componentFaces';
import {
    DragLimits,
    MIN_DRAG_HEIGHT,
    useDragGesture,
} from 'components/customer/Product/Drawer/helpers/useDragGesture';

interface UseComponentFaceDragProps {
    faces: Face[];
    total: number;
    ratio: number;
    setHeight: (index: number, height: number) => void;
}

// Dragging a boundary on a drawer face panel. Only the faces still on auto
// give up room, so the dragged face is the one that ends up set.
export const useComponentFaceDrag = ({
    faces,
    total,
    ratio,
    setHeight,
}: UseComponentFaceDragProps) => {
    const limits = useCallback(
        (dragged: number): DragLimits => {
            const draggedHeight = faces[Number(dragged)]?.height ?? 0;
            const absorbers = faces.filter(
                (face, index) => index !== dragged && face.isAuto
            );
            const spare = absorbers.reduce((sum, face) => sum + face.height, 0);

            return {
                draggedHeight,
                max: draggedHeight + spare - absorbers.length * MIN_DRAG_HEIGHT,
            };
        },
        [faces]
    );

    const settle = useCallback(
        (dragged: number, nextHeight: number) =>
            setFaceHeight(
                {
                    heights: faces.map((face) => face.height),
                    auto: faces.map((face) => face.isAuto),
                },
                total,
                dragged,
                nextHeight
            ).heights,
        [faces, total]
    );

    return useDragGesture({ratio, limits, settle, commit: setHeight});
};
